import { Typography, Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { IBreadcrumbsAndMenu, IAttribute } from "../../../types/global";

import { useState } from "react";
import { useSnackbar } from "notistack";
import { useParams, useSearchParams } from "react-router-dom";
import { GraphQLClient } from "graphql-request";

import { useModal } from "../../../hooks/use-modal";
import { createGraphQLClientWithMiddleware } from "../../../services/graphqlClient";

import {
  SetItemAttributeQuery,
  ItemAttributeFindUniqueQuery,
  ItemAttributesFindManyQuery,
  useSetItemAttributeQuery,
  useItemAttributeCreateMutation,
  useItemAttributeDeleteMutation,
  useItemAttributeFindUniqueQuery,
  useItemAttributeUpdateMutation,
  useItemAttributesFindManyQuery,
} from "../../../generated/general";

import AttributeTable from "../../../components/Table/Setting/Global/AttributeTable";
import CustomizedButton from "../../../components/Custom/CustomizedButton";
import ConfirmationModal from "../../../components/UI/Modal/ConfirmationModal";
import CustomizedBreadcrumbs from "../../../components/Custom/CustomizedBreadcrumbs";

import AddIcon from "@mui/icons-material/Add";
import AttributeModal from "../../../components/UI/Modal/AttributeModal";

const AttributeSetting = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const [deletingId, setDeletingId] = useState<number>();

  let [searchParams, setSearchParams] = useSearchParams();

  const attribute_id = searchParams.get("attribute_id");

  const setSearchParamsHandler = (key: string, value: string) => {
    searchParams.set(key, value);
    setSearchParams(searchParams);
  };

  const removeSearchParamsHandler = (key: string) => {
    searchParams.delete(key);
    setSearchParams(searchParams);
  };

  const { modal, openModalHandler, closeModalHandler } = useModal(
    undefined,
    () => removeSearchParamsHandler("attribute_id")
  );

  const { enqueueSnackbar } = useSnackbar();

  const graphQLClientGeneral: GraphQLClient =
    createGraphQLClientWithMiddleware("general");

  const { data: setItemAttributeData } =
    useSetItemAttributeQuery<SetItemAttributeQuery>(graphQLClientGeneral, {
      uniqueInput: {
        id: id ? parseInt(id) : undefined,
      },
    });

  const { data: rowData, refetch: refetchRowData } =
    useItemAttributesFindManyQuery<ItemAttributesFindManyQuery>(
      graphQLClientGeneral,
      {
        setAttributeId: id ? parseInt(id) : undefined,
      }
    );

  const { data, isLoading, isSuccess } =
    useItemAttributeFindUniqueQuery<ItemAttributeFindUniqueQuery>(
      graphQLClientGeneral,
      {
        uniqueInput: {
          id: attribute_id ? parseInt(attribute_id) : undefined,
        },
      },
      {
        enabled: Boolean(attribute_id),
      }
    );

  const { mutateAsync: createAttribute } =
    useItemAttributeCreateMutation<Error>(graphQLClientGeneral);

  const { mutateAsync: updateAttribute } =
    useItemAttributeUpdateMutation<Error>(graphQLClientGeneral);

  const { mutateAsync: deleteAttribute } =
    useItemAttributeDeleteMutation<Error>(graphQLClientGeneral);

  const breadcrumbs: IBreadcrumbsAndMenu[] = [
    {
      name: t("setting.index"),
      to: "/setting",
    },
    {
      name: t("setting.inventory.index"),
      to: "/setting/inventory",
    },
    {
      name: t("inventory.items.set_attribute"),
      to: "/setting/inventory/attribute",
    },
    {
      name: setItemAttributeData?.SetItemAttribute?.name_th ?? id ?? "",
    },
  ];

  async function onAddHandler(data: IAttribute) {
    const formatData = {
      ...data,
      set_item_attribute_id: parseInt(id ?? "0"),
      sorting_index: rowData?.ItemAttributesFindMany.length ?? 0,
    };
    try {
      await createAttribute({
        data: formatData,
      });
      enqueueSnackbar(`เพิ่ม${t("setting.inventory.attribute")}สำเร็จ`, {
        variant: "success",
      });
      await refetchRowData();
    } catch (err) {
      enqueueSnackbar(`เพิ่ม${t("setting.inventory.attribute")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  }

  const onEditHandler = (attribute_id?: number) => {
    if (attribute_id) {
      setSearchParamsHandler("attribute_id", attribute_id.toString());
      openModalHandler();
    }
  };

  const onUpdateHandler = async (data: IAttribute) => {
    try {
      const { id, set_item_attribute_id, ...otherData } = data;
      await updateAttribute({
        uniqueInput: {
          id: id,
        },
        data: otherData,
      });
      enqueueSnackbar(`แก้ไข${t("setting.inventory.attribute")}สำเร็จ`, {
        variant: "success",
      });
      await refetchRowData();
    } catch (err) {
      enqueueSnackbar(`แก้ไข${t("setting.inventory.attribute")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const onDeleteHandler = (attribute_id?: number) => {
    if (attribute_id) {
      setDeletingId(attribute_id);
      openConfirmationHandler();
    }
  };

  const deleteActionHandler = async () => {
    try {
      await deleteAttribute({
        uniqueInput: {
          id: deletingId,
        },
      });
      setDeletingId(undefined);
      enqueueSnackbar(`ลบ${t("inventory.items.attribute")}สำเร็จ`, {
        variant: "success",
      });
      await refetchRowData();
    } catch (err) {
      enqueueSnackbar(`ลบ${t("inventory.items.attribute")}ไม่สำเร็จ`, {
        variant: "error",
      });
    }
  };

  const {
    modal: confirmation,
    openModalHandler: openConfirmationHandler,
    closeModalHandler: closeConfirmationHandler,
    submitModalHandler: submitConfirmationHandler,
  } = useModal(deleteActionHandler);

  return (
    <>
      <CustomizedBreadcrumbs breadcrumbs={breadcrumbs} />
      <Box sx={{ display: "flex", justifyContent: "space-between", my: 3 }}>
        <Typography variant="h5">{t("inventory.items.attribute")}</Typography>
        <CustomizedButton
          title={t("button.add") + t("inventory.items.attribute")}
          variant="contained"
          onClick={openModalHandler}
          sx={{ textTransform: "none" }}
          endIcon={<AddIcon />}
        />
      </Box>
      <AttributeTable
        data={rowData?.ItemAttributesFindMany || []}
        onEditHandler={onEditHandler}
        onDeleteHandler={onDeleteHandler}
      />
      <AttributeModal
        open={modal}
        closeModalHandler={closeModalHandler}
        onAddHandler={onAddHandler}
        onUpdateHandler={onUpdateHandler}
        data={data?.ItemAttributeFindUnique}
        isLoading={isLoading}
        isSuccess={isSuccess}
      />
      <ConfirmationModal
        title={`ยืนยันการลบ${t("inventory.items.attribute")}`}
        open={confirmation}
        handleClose={closeConfirmationHandler}
        action={submitConfirmationHandler}
      />
    </>
  );
};

export default AttributeSetting;
